"use client";
/**
 * SANATAN DECODE — style proof v6. 21s @ 30fps, 1280x720.
 *
 * Yash on v5: the halftone dots are too much; the flat paper background can't stay; we need a
 * LIVE background — realistic imagery of thousands of years ago; and piling on more vector
 * shapes is not the answer.
 *
 * So v6 changes the FOUNDATION, not the decoration:
 *   - The ground is now photoreal gpt-image-2 plates of ancient India (yajna at dawn, palm-leaf
 *     manuscript by lamplight, a village lineage, a Vedic wedding) plus two modern plates.
 *     Each one lives: continuous Ken Burns push + pan, and beats change by a PUSH/SLIDE, never
 *     a dissolve.
 *   - Halftone dots: GONE. Mandala, torans, kalash, rings, dot-grids: GONE. The only graphics
 *     left are the ones that carry the argument — the bloodline thread and the inheritance
 *     diagram — plus type.
 *   - Kept from v5 because they were working: animate ON TWOS, object permanence (the thread is
 *     one object that becomes the inheritance V), word timings from the real narration, and a
 *     single accent colour.
 *   - New: a split-frame at the lineage beat — ancient on the left, present day on the right,
 *     the thread crossing the seam. That is the whole thesis in one frame.
 */
import React from "react";
import {
  AbsoluteFill, Audio, Img, staticFile,
  useCurrentFrame, useVideoConfig, interpolate, spring, Easing, random,
} from "remotion";
import { loadFont as loadDeva } from "@remotion/google-fonts/NotoSansDevanagari";
import { loadFont as loadInter } from "@remotion/google-fonts/Inter";

const { fontFamily: deva } = loadDeva("normal", { weights: ["400", "600", "700", "800", "900"], subsets: ["devanagari", "latin"] });
const { fontFamily: inter } = loadInter("normal", { weights: ["500", "700", "800"], subsets: ["latin"] });

const CREAM = "#F7EEDC";
const ACCENT = "#F0913A";      // THE accent — reads on dark photography, unlike deep kumkum
const GOLD = "#C9A55A";
const DIM = "#CFC2AA";

const FPS = 30;
const f = (s: number) => s * FPS;
const useStep = () => Math.floor(useCurrentFrame() / 2) * 2;   // Vox animates on twos

const W: Record<number, [number, number, string]> = {
  0: [0.0, 0.15, "आप"], 1: [0.21, 0.35, "जिस"], 2: [0.41, 0.71, "गोत्र"], 3: [0.74, 0.82, "का"],
  4: [0.87, 1.06, "नाम"], 5: [1.13, 1.36, "लेते"], 6: [1.42, 1.64, "हैं"],
  8: [2.03, 2.16, "वो"], 9: [2.26, 2.47, "आपका"], 10: [2.58, 3.01, "सरनेम"], 11: [3.05, 3.24, "नहीं"],
  12: [3.32, 3.6, "है।"],
  15: [4.35, 4.7, "ऋषि"],
  26: [8.2, 8.42, "एक"], 27: [8.45, 8.52, "ही"], 28: [8.63, 9.17, "रक्त-रेखा"],
  34: [11.61, 12.03, "शास्त्र"],
  38: [13.41, 14.03, "सगोत्र"], 39: [14.41, 14.76, "विवाह"], 40: [14.86, 15.27, "नहीं।"],
  45: [17.43, 17.86, "विज्ञान"],
};

const useSpringAt = (at: number, damping = 9, stiffness = 110, mass = 1.1) => {
  const frame = useStep();
  const { fps } = useVideoConfig();
  return spring({ frame: frame - f(at), fps, config: { damping, stiffness, mass } });
};

/* ══ THE LIVE GROUND ═══════════════════════════════════════════
   Photoreal plate, always moving, arriving by push — never a dissolve.            */
const Plate: React.FC<{
  src: string; at: number; zoom?: [number, number]; pan?: [number, number];
  from?: "r" | "l" | "b"; warm?: number; z?: number;
}> = ({ src, at, zoom = [1.06, 1.18], pan = [0, -34], from = "r", warm = 0, z = 0 }) => {
  const frame = useStep();
  if (frame < f(at) - 16) return null;
  const push = interpolate(frame, [f(at), f(at) + 15], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic),
  });
  const dx = from === "r" ? push * 1280 : from === "l" ? -push * 1280 : 0;
  const dy = from === "b" ? push * 720 : 0;
  // Ken Burns runs for the whole life of the plate — the image is never still.
  const k = interpolate(frame - f(at), [0, 300], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const sc = zoom[0] + (zoom[1] - zoom[0]) * k;
  const px = pan[0] * k;
  const py = pan[1] * k;
  return (
    <AbsoluteFill style={{ transform: `translate(${dx}px, ${dy}px)`, zIndex: z, overflow: "hidden" }}>
      <Img src={staticFile(src)} style={{
        width: "100%", height: "100%", objectFit: "cover",
        transform: `scale(${sc}) translate(${px}px, ${py}px)`,
        filter: warm ? `sepia(${warm}) saturate(1.15) hue-rotate(-8deg)` : undefined,
      }} />
    </AbsoluteFill>
  );
};

/** Warm scrim so Devanagari always has a floor to sit on. */
const Scrim: React.FC<{ side?: "b" | "l"; strength?: number }> = ({ side = "b", strength = 0.82 }) => (
  <AbsoluteFill style={{
    pointerEvents: "none",
    background: side === "b"
      ? `linear-gradient(to top, rgba(22,14,6,${strength}) 0%, rgba(22,14,6,${strength * 0.72}) 26%, rgba(22,14,6,0) 62%)`
      : `linear-gradient(to right, rgba(22,14,6,${strength}) 0%, rgba(22,14,6,0) 58%)`,
  }} />
);

const Vignette: React.FC = () => (
  <AbsoluteFill style={{ pointerEvents: "none", background: "radial-gradient(ellipse 76% 72% at 50% 46%, rgba(0,0,0,0) 42%, rgba(0,0,0,0.42) 100%)" }} />
);

const Grain: React.FC = () => {
  const frame = useStep();
  return (
    <AbsoluteFill style={{ pointerEvents: "none", opacity: 0.09, mixBlendMode: "overlay" }}>
      <svg width="100%" height="100%">
        <filter id="g6"><feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="3" seed={frame % 5} /></filter>
        <rect width="100%" height="100%" filter="url(#g6)" />
      </svg>
    </AbsoluteFill>
  );
};

/** A few warm embers rising — motivated by the fire in the plates, not decoration. */
const Embers: React.FC = () => {
  const frame = useStep();
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      {Array.from({ length: 12 }, (_, i) => {
        const seed = `e${i}`;
        const x = random(seed) * 1280;
        const sp = 0.35 + random(seed + "s") * 0.55;
        const y = 760 - ((random(seed + "y") * 800 + frame * sp) % 840);
        const sway = Math.sin((frame + i * 55) / 40) * 26;
        const r = 1.6 + random(seed + "r") * 2.4;
        const tw = 0.25 + 0.35 * (0.5 + 0.5 * Math.sin((frame + i * 30) / 12));
        return <div key={i} style={{
          position: "absolute", left: x + sway, top: y, width: r * 2, height: r * 2,
          borderRadius: "50%", background: ACCENT, opacity: tw,
          boxShadow: `0 0 ${r * 4}px ${ACCENT}`,
        }} />;
      })}
    </AbsoluteFill>
  );
};

/* ══ TYPE ══════════════════════════════════════════════════════ */
const Word: React.FC<{
  idx: number; size: number; color?: string; weight?: number;
  strike?: boolean; outAt?: number;
}> = ({ idx, size, color = CREAM, weight = 800, strike, outAt }) => {
  const [start] = W[idx];
  const frame = useStep();
  const { fps } = useVideoConfig();
  const sIn = spring({ frame: frame - f(start), fps, config: { damping: 9, stiffness: 150, mass: 0.8 } });
  const sOut = outAt ? spring({ frame: frame - f(outAt), fps, config: { damping: 200, stiffness: 80, mass: 0.5 } }) : 0;
  const grow = interpolate(frame, [f(start) + 2, f(start) + 12], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });
  return (
    <span style={{
      position: "relative", display: "inline-block",
      fontFamily: deva, fontWeight: weight, fontSize: size, lineHeight: 1.36, color,
      textShadow: "0 3px 18px rgba(0,0,0,0.75)",
      opacity: Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4)),
      transform: `translateY(${(1 - sIn) * 30 + sOut * 80}px) rotate(${7 * (1 - sIn) - 12 * sOut}deg) scale(${0.88 + sIn * 0.12})`,
      transformOrigin: "50% 140%",
    }}>
      {W[idx][2]}
      {strike && <span style={{ position: "absolute", left: -4, top: "53%", height: Math.max(4, size * 0.075), width: `calc((100% + 8px) * ${grow})`, background: ACCENT, borderRadius: 4 }} />}
    </span>
  );
};

const Row: React.FC<{ children: React.ReactNode; gap?: number }> = ({ children, gap = 16 }) => (
  <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: `0 ${gap}px` }}>{children}</div>
);

/* ══ THE ONE PERSISTENT GRAPHIC ════════════════════════════════
   Bloodline thread → collapses to a dot → reopens as the inheritance V.           */
const PersistentThread: React.FC = () => {
  const frame = useStep();
  const Y = 402, X0 = 250, X1 = 1030;
  const CX = 430, PY = 250, CY = 404;

  const draw = interpolate(frame, [f(6.6), f(7.7)], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.cubic) });
  const collapse = interpolate(frame, [f(10.1), f(10.6)], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.cubic) });
  const reopen = interpolate(frame, [f(16.6), f(17.15)], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });
  const sway = Math.sin(frame / 21) * 8 * draw * (1 - collapse);
  if (frame < f(6.5)) return null;

  const dotX = interpolate(collapse, [0, 1], [(X0 + X1) / 2, 640]);
  const dotY = interpolate(collapse, [0, 1], [Y - 24, 300]);
  const px = interpolate(reopen, [0, 1], [dotX, CX]);
  const py = interpolate(reopen, [0, 1], [dotY, CY]);

  return (
    <svg width={1280} height={720} style={{ position: "absolute", pointerEvents: "none" }}>
      <g opacity={1 - collapse}>
        <path
          d={`M${X0} ${Y} Q ${(X0 + X1) / 2} ${Y - 24 - sway} ${X0 + (X1 - X0) * draw} ${Y - (24 + sway) * Math.sin(Math.PI * draw)}`}
          fill="none" stroke={ACCENT} strokeWidth={4} strokeLinecap="round"
          style={{ filter: "drop-shadow(0 0 10px rgba(240,145,58,0.8))" }}
        />
        {[0.3, 0.5, 0.7].map((t, i) => draw > t ? (
          <circle key={i} cx={X0 + (X1 - X0) * t} cy={Y - (24 + sway) * Math.sin(Math.PI * t)} r={6} fill={CREAM} stroke={ACCENT} strokeWidth={3} />
        ) : null)}
      </g>
      <circle cx={px} cy={py} r={6 * collapse * (1 - reopen) + 4 * reopen} fill={ACCENT} opacity={collapse}
        style={{ filter: "drop-shadow(0 0 12px rgba(240,145,58,0.9))" }} />
      <g opacity={reopen} style={{ filter: "drop-shadow(0 0 10px rgba(240,145,58,0.7))" }}>
        <line x1={CX} y1={CY} x2={CX - 108 * reopen} y2={CY - (CY - PY - 38) * reopen} stroke={ACCENT} strokeWidth={4} strokeLinecap="round" />
        <line x1={CX} y1={CY} x2={CX + 108 * reopen} y2={CY - (CY - PY - 38) * reopen} stroke={ACCENT} strokeWidth={4} strokeLinecap="round" />
      </g>
    </svg>
  );
};

const Beat: React.FC<{ from: number; to: number; children: React.ReactNode }> = ({ from, to, children }) => {
  const frame = useStep();
  if (frame < f(from - 0.5) || frame >= f(to + 0.6)) return null;
  return <AbsoluteFill>{children}</AbsoluteFill>;
};

/* ══ BEATS ═════════════════════════════════════════════════════ */

const BeatA: React.FC = () => (
  <>
    <Scrim strength={0.86} />
    <div style={{ position: "absolute", left: 0, right: 0, bottom: 96 }}>
      <Row gap={13}>{[0, 1, 2, 3, 4, 5, 6].map((i) => <Word key={i} idx={i} size={34} color={DIM} weight={600} outAt={3.6} />)}</Row>
      <div style={{ height: 12 }} />
      <Row gap={15}>
        <Word idx={8} size={70} weight={900} outAt={3.62} />
        <Word idx={9} size={70} weight={900} outAt={3.64} />
        <Word idx={10} size={70} weight={900} color={ACCENT} strike outAt={3.66} />
        <Word idx={11} size={70} weight={900} outAt={3.68} />
        <Word idx={12} size={70} weight={900} outAt={3.7} />
      </Row>
    </div>
  </>
);

const BeatB: React.FC = () => {
  const halo = useSpringAt(4.35, 12, 80);
  const gone = useSpringAt(5.5, 200, 70);
  return (
    <>
      <Scrim strength={0.8} />
      <svg width={1280} height={720} style={{ position: "absolute", opacity: halo * 0.5 * (1 - gone) }}>
        <circle cx={430} cy={250} r={120 + halo * 40} fill="none" stroke={ACCENT} strokeWidth={2}
          style={{ filter: "drop-shadow(0 0 12px rgba(240,145,58,0.8))" }} />
      </svg>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 108, textAlign: "center" }}>
        <Row><Word idx={15} size={86} weight={900} color={ACCENT} outAt={5.52} /></Row>
      </div>
    </>
  );
};

/** Split frame — ancient left, present right, thread crossing the seam. */
const BeatC: React.FC = () => {
  const frame = useStep();
  const split = interpolate(frame, [f(7.0), f(7.5)], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });
  const seam = 700;
  return (
    <>
      {/* present day slides in over the right side */}
      <AbsoluteFill style={{ clipPath: `inset(0 0 0 ${seam - (1280 - seam) * (1 - split)}px)` }}>
        <Plate src="sanatan/bg/bg_modern.png" at={7.0} zoom={[1.08, 1.16]} pan={[-20, 0]} from="r" z={1} />
      </AbsoluteFill>
      <div style={{ position: "absolute", left: seam - 2, top: 0, width: 3, height: 720, background: GOLD, opacity: split * 0.85 }} />
      <Scrim strength={0.84} />
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 92, textAlign: "center" }}>
        <Row gap={14}>
          <Word idx={26} size={46} weight={800} outAt={10.2} />
          <Word idx={27} size={46} weight={800} outAt={10.22} />
          <Word idx={28} size={64} weight={900} color={ACCENT} outAt={10.24} />
        </Row>
      </div>
    </>
  );
};

const BeatDE: React.FC = () => {
  const tag = useSpringAt(11.61, 10, 150);
  const tagOut = useSpringAt(15.4, 200, 80);
  return (
    <>
      <Scrim strength={0.88} />
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 92, textAlign: "center" }}>
        <div style={{
          display: "inline-block", fontFamily: deva, fontSize: 21, fontWeight: 800, letterSpacing: 6,
          color: "#1B1206", background: ACCENT, padding: "6px 22px", borderRadius: 3,
          opacity: Math.min(1, tag * 1.5) * (1 - tagOut),
          transform: `translateY(${(1 - tag) * 18}px) rotate(${5 * (1 - tag)}deg)`,
        }}>{W[34][2]}</div>
        <div style={{ height: 16 }} />
        <Row gap={18}>
          <Word idx={38} size={76} weight={900} outAt={15.4} />
          <Word idx={39} size={76} weight={900} outAt={15.42} />
          <Word idx={40} size={76} weight={900} color={ACCENT} outAt={15.44} />
        </Row>
      </div>
    </>
  );
};

const BeatF: React.FC = () => {
  const frame = useStep();
  const CX = 430, PY = 250, CY = 404;
  const pA = useSpringAt(15.9, 9, 140);
  const pB = useSpringAt(16.2, 9, 140);
  const kid = useSpringAt(17.15, 8, 150);
  const term = useSpringAt(17.43, 10, 120);

  const Allele: React.FC<{ x: number; fill: string; op?: number }> = ({ x, fill, op = 1 }) => (
    <rect x={x - 6} y={-18} width={12} height={36} rx={6} fill={fill} opacity={op} />
  );
  const Carrier: React.FC<{ x: number; s: number; ph: number }> = ({ x, s, ph }) => (
    <g opacity={s} transform={`translate(${x},${PY + Math.sin((frame + ph) / 26) * 5}) scale(${0.8 + s * 0.2}) rotate(${8 * (1 - s)})`}>
      <circle r={40} fill="rgba(18,12,5,0.72)" stroke={CREAM} strokeWidth={3} />
      <Allele x={-13} fill={CREAM} op={0.4} />
      <Allele x={13} fill={ACCENT} />
    </g>
  );

  return (
    <>
      <Scrim strength={0.8} />
      <svg width={1280} height={720} style={{ position: "absolute" }}>
        <Carrier x={CX - 122} s={pA} ph={0} />
        <Carrier x={CX + 122} s={pB} ph={40} />
        <g opacity={kid} transform={`translate(${CX},${CY + Math.sin(frame / 24) * 4}) scale(${0.78 + kid * 0.22})`}>
          <circle r={42} fill="rgba(240,145,58,0.22)" stroke={ACCENT} strokeWidth={4} />
          <Allele x={-13} fill={ACCENT} />
          <Allele x={13} fill={ACCENT} />
        </g>
      </svg>
      <div style={{
        position: "absolute", left: 0, right: 0, bottom: 84, textAlign: "center",
        opacity: Math.min(1, term * 1.4),
        transform: `translateY(${(1 - term) * 30}px)`,
      }}>
        <div style={{ fontFamily: inter, fontSize: 44, fontWeight: 800, letterSpacing: 7, color: ACCENT, textShadow: "0 3px 20px rgba(0,0,0,0.8)" }}>
          CONSANGUINITY
        </div>
        <div style={{ height: 10 }} />
        <div style={{ fontFamily: deva, fontSize: 26, fontWeight: 700, color: CREAM, textShadow: "0 2px 14px rgba(0,0,0,0.8)" }}>
          विज्ञान ने इसे एक नाम दिया।
        </div>
      </div>
    </>
  );
};

const Furniture: React.FC = () => {
  const frame = useStep();
  const { durationInFrames } = useVideoConfig();
  const p = interpolate(frame, [0, durationInFrames], [0, 1]);
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <div style={{ position: "absolute", top: 40, left: 50, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 4, height: 18, background: ACCENT, borderRadius: 2 }} />
        <span style={{ fontFamily: deva, fontWeight: 700, fontSize: 17, letterSpacing: 3, color: CREAM, opacity: 0.8, textShadow: "0 2px 10px rgba(0,0,0,0.8)" }}>
          सनातन डिकोड
        </span>
      </div>
      <div style={{ position: "absolute", left: 0, bottom: 0, height: 3, width: `${p * 100}%`, background: ACCENT, opacity: 0.9 }} />
    </AbsoluteFill>
  );
};

export const SanatanDecodeGotraV6: React.FC = () => (
  <AbsoluteFill style={{ backgroundColor: "#120C05", overflow: "hidden" }}>
    <Audio src={staticFile("sanatan/gotra.mp3")} />

    {/* the live ground — each plate pushes the last one off, then never stops moving */}
    <Plate src="sanatan/bg/bg_manuscript.png" at={0} zoom={[1.05, 1.16]} pan={[-26, -14]} from="r" />
    <Plate src="sanatan/bg/bg_yajna.png" at={3.9} zoom={[1.06, 1.17]} pan={[22, -16]} from="r" />
    <Plate src="sanatan/bg/bg_lineage.png" at={5.8} zoom={[1.05, 1.15]} pan={[-24, -10]} from="l" />
    <Plate src="sanatan/bg/bg_vivah.png" at={10.6} zoom={[1.06, 1.18]} pan={[18, -18]} from="r" />
    <Plate src="sanatan/bg/bg_science.png" at={15.8} zoom={[1.06, 1.16]} pan={[-20, -12]} from="b" warm={0.42} />

    <AbsoluteFill style={{ zIndex: 10 }}>
      <Beat from={0} to={3.75}><BeatA /></Beat>
      <Beat from={3.9} to={5.6}><BeatB /></Beat>
      <Beat from={5.8} to={10.4}><BeatC /></Beat>
      <Beat from={10.6} to={15.6}><BeatDE /></Beat>
      <Beat from={15.8} to={21.1}><BeatF /></Beat>

      <PersistentThread />

      <Embers />
      <Vignette />
      <Grain />
      <Furniture />
    </AbsoluteFill>
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
